import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Users, MonitorSmartphone, CalendarCheck, ShieldCheck } from 'lucide-react';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
type TabType = 'pribadi' | 'sosial' | 'belajar' | 'karier';
type BookingType = 'individu' | 'kelompok' | 'eguide' | null;
const bimbinganData = {
  pribadi: {
    title: 'Bimbingan Pribadi',
    desc: 'Layanan untuk membantu siswa mengembangkan pribadi yang mandiri, beriman, dan sehat secara mental.'
  },
  sosial: {
    title: 'Bimbingan Sosial',
    desc: 'Membantu siswa berinteraksi dan menyesuaikan diri dengan lingkungan sosial secara positif.'
  },
  belajar: {
    title: 'Bimbingan Belajar',
    desc: 'Membantu meningkatkan kebiasaan belajar dan prestasi akademik secara optimal.'
  },
  karier: {
    title: 'Bimbingan Karier',
    desc: 'Membantu siswa mengenali potensi dan merencanakan masa depan karier.'
  }
};
export function Bimbingan() {
  const [activeTab, setActiveTab] = useState<TabType>('pribadi');
  const [bookingType, setBookingType] = useState<BookingType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nama_lengkap: '',
    kelas: '',
    no_hp: '',
    topik: '',
    metode_virtual: '',
    jadwal: ''
  });

  // ================= SUBMIT (FIX TIMEZONE) =================
  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const {
      data: userData
    } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) {
      setIsSubmitting(false);
      return;
    }
    const {
      error
    } = await supabase.from('bimbingan_bookings').insert([{
      user_id: user.id,
      nama_lengkap: form.nama_lengkap,
      kelas: form.kelas,
      no_hp: form.no_hp,
      topik: form.topik,
      metode: bookingType,
      // 🔥 FIX IMPORTANT
      metode_virtual: bookingType === 'eguide' ? form.metode_virtual : null,
      // ✅ SIMPAN UTC (ANTI GESER JAM)
      jadwal: new Date(form.jadwal).toISOString()
    }]);
    setIsSubmitting(false);
    if (error) {
      alert('Gagal mengirim: ' + error.message);
      return;
    }
    setBookingType(null);
    setShowSuccess(true);
    setForm({
      nama_lengkap: '',
      kelas: '',
      no_hp: '',
      topik: '',
      metode_virtual: '',
      jadwal: ''
    });
  };

  // ================= FORMAT WIB =================
  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('id-ID', {
      timeZone: 'Asia/Jakarta',
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  // ================= MODAL =================
  const renderBookingForm = () => {
    if (!bookingType) return null;
    const titleMap: any = {
      individu: 'Jadwalkan Bimbingan Individu',
      kelompok: 'Jadwalkan Bimbingan Kelompok',
      eguide: 'Jadwalkan E-Guide Virtual'
    };
    return <Modal isOpen={!!bookingType} onClose={() => setBookingType(null)} title={titleMap[bookingType]} data-id="element-173">
        <form onSubmit={handleBookingSubmit} className="space-y-4" data-id="element-174">

          <Input label="Nama Lengkap" required value={form.nama_lengkap} onChange={e => setForm({
          ...form,
          nama_lengkap: e.target.value
        })} data-id="element-175" />

          <Input label="Kelas" required value={form.kelas} onChange={e => setForm({
          ...form,
          kelas: e.target.value
        })} data-id="element-176" />

          <Input label="No. HP / WhatsApp" required value={form.no_hp} onChange={e => setForm({
          ...form,
          no_hp: e.target.value
        })} data-id="element-177" />

          <Input label="Topik" as="textarea" required value={form.topik} onChange={e => setForm({
          ...form,
          topik: e.target.value
        })} data-id="element-178" />

          {bookingType === 'eguide' && <Input label="Metode Virtual" as="select" required value={form.metode_virtual} onChange={e => setForm({
          ...form,
          metode_virtual: e.target.value
        })} options={[{
          label: 'Chat',
          value: 'chat'
        }, {
          label: 'Call',
          value: 'call'
        }, {
          label: 'Video Call',
          value: 'vcall'
        }]} data-id="element-179" />}

          <Input label="Jadwal" type="datetime-local" required value={form.jadwal} onChange={e => setForm({
          ...form,
          jadwal: e.target.value
        })} data-id="element-180" />

          <Button type="submit" fullWidth isLoading={isSubmitting} data-id="element-181">
            Ajukan Jadwal
          </Button>

        </form>
      </Modal>;
  };
  return <div className="min-h-screen bg-white py-12" data-id="element-182">

      <div className="max-w-5xl mx-auto px-4" data-id="element-183">

        {/* HEADER */}
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-184">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-185">
            <h1 className="text-4xl font-bold text-dark mb-4" data-id="element-186">
              Layanan Bimbingan
            </h1>
            <p className="text-lg text-gray-600" data-id="element-187">
              Pilih layanan bimbingan sesuai kebutuhan.
            </p>
          </motion.div>
        </div>

        {/* RIWAYAT */}
        <div className="flex justify-center mb-8" data-id="element-188">
          <button onClick={() => navigate('/bimbingan_booking')} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm" data-id="element-189">
            📋 Riwayat Booking Saya
          </button>
        </div>

        {/* TAB */}
        <div className="grid md:grid-cols-4 gap-3 mb-10" data-id="element-190">
          {(Object.keys(bimbinganData) as TabType[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`py-3 rounded-2xl text-sm font-medium ${activeTab === tab ? 'bg-dark text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-191">
              {bimbinganData[tab].title}
            </button>)}
        </div>

        {/* CONTENT */}
        <motion.div key={activeTab} initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="bg-white rounded-3xl p-8 border shadow-sm mb-10" data-id="element-192">
          <h2 className="text-2xl font-bold mb-2" data-id="element-193">
            {bimbinganData[activeTab].title}
          </h2>
          <p className="text-gray-600" data-id="element-194">
            {bimbinganData[activeTab].desc}
          </p>
        </motion.div>

        {/* CARDS */}
        <div className="grid md:grid-cols-3 gap-8" data-id="element-195">

          <div className="bg-white p-8 rounded-3xl border shadow-sm flex flex-col" data-id="element-196">
            <User className="mb-4 text-blue-600" data-id="element-197" />
            <h3 className="font-bold mb-2" data-id="element-198">Individu</h3>
            <Button fullWidth onClick={() => setBookingType('individu')} data-id="element-199">
              Booking
            </Button>
          </div>

          <div className="bg-white p-8 rounded-3xl border shadow-sm flex flex-col" data-id="element-200">
            <Users className="mb-4 text-pink-600" data-id="element-201" />
            <h3 className="font-bold mb-2" data-id="element-202">Kelompok</h3>
            <Button fullWidth onClick={() => setBookingType('kelompok')} data-id="element-203">
              Booking
            </Button>
          </div>

          <div className="bg-white p-8 rounded-3xl border shadow-sm flex flex-col" data-id="element-204">
            <MonitorSmartphone className="mb-4 text-gray-700" data-id="element-205" />
            <h3 className="font-bold mb-2" data-id="element-206">E-Guide</h3>
            <Button fullWidth onClick={() => setBookingType('eguide')} data-id="element-207">
              Booking
            </Button>
          </div>

        </div>

        {/* MODAL */}
        {renderBookingForm()}

        {/* SUCCESS POPUP */}
        <Modal isOpen={showSuccess} onClose={() => setShowSuccess(false)} title="Berhasil" data-id="element-208">
          <div className="text-center py-6" data-id="element-209">
            <ShieldCheck className="mx-auto text-green-500 w-12 h-12 mb-3" data-id="element-210" />
            <p className="mb-4" data-id="element-211">Booking berhasil diajukan!</p>
            <Button fullWidth onClick={() => setShowSuccess(false)} data-id="element-212">
              OK
            </Button>
          </div>
        </Modal>

      </div>
    </div>;
}